import { useState, useRef, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import api from '../services/api';

// Configure pdf.js worker using Vite's ?url import
pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

/**
 * PdfPreview - Renders the first page of a real generated PDF as a preview.
 *
 * Props:
 *   toolType: string - the worksheet type ('cursive', 'print', etc.)
 *   settings: object - all the settings to pass to the generate API
 *   pageCount: number - (optional) total pages to display in subtitle
 */
export default function PdfPreview({ toolType, settings, pageCount }) {
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const prevSettingsRef = useRef(null);

  // Render PDF buffer onto canvas
  const renderPdf = useCallback(async (pdfData) => {
    try {
      const loadingTask = pdfjsLib.getDocument({ data: pdfData });
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);

      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');

      // Scale to fit the container nicely (aim for ~450px wide)
      const desiredWidth = 450;
      const unscaledViewport = page.getViewport({ scale: 1 });
      const scale = desiredWidth / unscaledViewport.width;
      const viewport = page.getViewport({ scale });

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({
        canvasContext: ctx,
        viewport: viewport
      }).promise;
    } catch (err) {
      console.error('PDF render error:', err);
    }
  }, []);

  // Generate PDF from the API
  const generatePreview = useCallback(async (currentSettings) => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/worksheets/generate', {
        toolType,
        ...currentSettings
      }, {
        responseType: 'arraybuffer',
        signal: controller.signal
      });

      // Check if this request was aborted
      if (controller.signal.aborted) return;

      await renderPdf(response.data);
      setInitialLoad(false);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return; // Request was cancelled, ignore
      }
      console.error('Preview generation error:', err);
      setError('Preview unavailable');
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [toolType, renderPdf]);

  // Debounced effect that triggers on settings changes
  useEffect(() => {
    // Serialize settings to detect actual changes
    const settingsKey = JSON.stringify({ toolType, ...settings });
    if (prevSettingsRef.current === settingsKey) return;
    prevSettingsRef.current = settingsKey;

    // Clear previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // On initial load, generate immediately; otherwise debounce
    const delay = initialLoad ? 0 : 800;

    debounceTimerRef.current = setTimeout(() => {
      generatePreview(settings);
    }, delay);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [toolType, settings, generatePreview, initialLoad]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const pageText = pageCount && pageCount > 1
    ? `This worksheet will be ${pageCount} pages`
    : 'This worksheet will be 1 page';

  return (
    <div className="lg:sticky lg:top-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Worksheet Preview</h3>
        <p className="text-gray-500 text-sm mb-4">{pageText}</p>

        <div className="bg-gray-100 rounded-lg p-4 flex items-center justify-center relative">
          {/* Loading overlay */}
          {loading && (
            <div className="absolute inset-0 bg-white/60 rounded-lg flex items-center justify-center z-10">
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
                <span className="text-sm text-gray-500">Generating preview...</span>
              </div>
            </div>
          )}

          {/* Initial loading state (before first render) */}
          {initialLoad && !error && (
            <div className="flex flex-col items-center gap-2 py-20">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              <span className="text-sm text-gray-400">Loading preview...</span>
            </div>
          )}

          {/* Error state */}
          {error && !loading && (
            <div className="text-center py-20">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* PDF canvas */}
          <canvas
            ref={canvasRef}
            className={`bg-white shadow-md ${initialLoad ? 'hidden' : ''}`}
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </div>
      </div>
    </div>
  );
}
